import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSSN } from '../utils/dateUtils';
import { useOrganization } from '../contexts/OrganizationContext';

function Page5({ formData, updateFormData, onNext, onBack }) {
  const { t } = useTranslation();
  const { organization } = useOrganization();

  // Auto-populate name and SSN from Page 1 when component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Combine the taxpayer name from Page 1
    const fullName = `${formData.taxpayerFirstName || ''} ${formData.taxpayerMiddleName || ''} ${formData.taxpayerLastName || ''}`.trim();
    
    // Only update if feesName is empty (to avoid overwriting user edits)
    if (!formData.feesName && fullName) {
      updateFormData({ feesName: fullName });
    }
    
    // Only update if feesSsn is empty (to avoid overwriting user edits)
    if (!formData.feesSsn && formData.ssn) {
      updateFormData({ feesSsn: formData.ssn });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  const handleSSNChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatSSN(value);
    updateFormData({ [name]: formatted });
  };

  const handleNext = (e) => {
    e.preventDefault();
    onNext();
  };

  return (
    <form onSubmit={handleNext}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('page5.header.title')}
        </h1>
        <p className="text-gray-600">{t('page5.header.subtitle')}</p>
      </div>

      {/* Authorization Statement */}
      <div className="mb-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="mb-4">
          <p className="text-sm text-gray-800 inline">
            {t('page5.authorization.intro')}
          </p>
          <input
            type="text"
            name="feesName"
            value={formData.feesName}
            onChange={handleInputChange}
            placeholder={t('page5.authorization.namePlaceholder')}
            className="mx-2 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent inline-block w-64"
            required
          />
          <span className="text-red-500">{t('common.required')}</span>
          <p className="text-sm text-gray-800 inline">, </p>
          <input
            type="text"
            inputMode="numeric"
            name="feesSsn"
            value={formData.feesSsn}
            onChange={handleSSNChange}
            placeholder="XXX-XX-XXXX"
            maxLength="11"
            className="mx-2 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent inline-block w-64"
            required
          />
          <span className="text-red-500">{t('common.required')}</span>
          <p className="text-sm text-gray-800 inline">
            {t('page5.authorization.text1', { organizationName: organization?.name || 'Tax Services' })}
          </p>
        </div>

        <p className="text-sm text-gray-800 mb-4">
          {t('page5.authorization.text2', { organizationName: organization?.name || 'Tax Services' })}
        </p>
      </div>

      {/* Fee Breakdown */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('page5.fees.title')}</h2>
        
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-200">
            <span className="text-sm font-medium text-gray-800">{t('page5.fees.processingFee')}</span>
            <span className="text-sm font-semibold text-gray-900">$20.00</span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-200">
            <span className="text-sm font-medium text-gray-800">{t('page5.fees.bankFee')}</span>
            <span className="text-sm font-semibold text-gray-900">$44.95</span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded border border-gray-200">
            <span className="text-sm font-medium text-gray-800">{t('page5.fees.transmitterFee')}</span>
            <span className="text-sm font-semibold text-gray-900">$232.95</span>
          </div>
          
          <div className="flex justify-between items-center p-3 bg-blue-100 rounded border border-blue-300">
            <span className="text-sm font-semibold text-gray-900">{t('page5.fees.taxPrepFee')}</span>
            <span className="text-sm font-bold text-blue-900">{t('page5.fees.agreedUponFiling')}</span>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <p className="text-sm text-gray-800 leading-relaxed">
          {t('page5.notice', { organizationName: organization?.name || 'Tax Services' })}
        </p>
      </div>

      {/* Agreement Confirmation */}
      <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-sm text-gray-700 font-medium">
          {t('page5.agreement')}
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          className="w-1/2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors font-medium text-lg"
        >
          {t('common.back')}
        </button>
        <button
          type="submit"
          className="w-1/2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
        >
          {t('common.next')}
        </button>
      </div>
    </form>
  );
}

export default Page5;
